import Chart from 'chart.js/auto';

const chartInstances = new Map();

function isMobileViewport() {
    return window.matchMedia('(max-width: 767.98px)').matches;
}

// Aligns the "Pierwszy dzień metamorfozy" / "Ostatni dzień metamorfozy" labels
// with the actual plot area (chart.chartArea), since its left edge shifts with
// the width of the Y axis tick labels and can't be pinned with static CSS.
//
// chart.chartArea/point coordinates are relative to the <canvas> element's own
// origin, but these labels are positioned relative to .chart-canvas (the
// canvas's *padded* parent) - so the parent's padding has to be added back in,
// otherwise everything lands padding-size too far up/left.
function syncAxisLabels(chart) {
    if (!chart.chartArea) {
        return;
    }

    const wrap = chart.canvas.parentElement;
    const axis = wrap ? wrap.querySelector('.metamorphosis-efekty__chart-axis') : null;
    if (!axis) {
        return;
    }

    const [first, last] = axis.querySelectorAll('span');
    if (!first || !last) {
        return;
    }

    const { left, right } = chart.chartArea;
    const offsetLeft = chart.canvas.offsetLeft;
    first.style.left = (offsetLeft + left) + 'px';
    last.style.right = (wrap.clientWidth - offsetLeft - right) + 'px';
}

// Value labels are real DOM elements laid over the canvas, not drawn onto it.
// Canvas text (ctx.fillText) never gets the browser's font hinting/subpixel
// anti-aliasing that native text does, so however precisely it's positioned
// it still reads as "soft" next to actual HTML text - most noticeable at the
// small size these badges use on mobile. Same approach as the axis labels.
function clearValueLabels(wrap) {
    wrap.querySelectorAll('.metamorphosis-efekty__value-label').forEach((el) => el.remove());
}

function renderValueLabels(chart) {
    if (!chart.chartArea) {
        return;
    }

    const wrap = chart.canvas.parentElement;
    if (!wrap) {
        return;
    }

    clearValueLabels(wrap);

    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);
    const unit = chart.canvas.dataset.unit || '';
    const mobile = isMobileViewport();
    const activeIndex = chart._metamorphosisActiveIndex ?? null;
    const offsetLeft = chart.canvas.offsetLeft;
    const offsetTop = chart.canvas.offsetTop;

    dataset.data.forEach((value, index) => {
        const isEndpoint = index === 0 || index === dataset.data.length - 1;
        if (mobile && !isEndpoint && activeIndex !== index) {
            return;
        }

        const point = meta.data[index];
        if (!point) {
            return;
        }

        const label = document.createElement('span');
        label.className = 'metamorphosis-efekty__value-label';
        if (index === 0) {
            // The first point sits right next to the Y axis - a centered
            // label would overlap it, so nudge it to the right instead.
            label.classList.add('metamorphosis-efekty__value-label--first');
        }
        label.textContent = value + (unit ? ' ' + unit : '');
        label.style.left = (offsetLeft + point.x) + 'px';
        label.style.top = (offsetTop + point.y - 14) + 'px';
        wrap.appendChild(label);
    });
}

// Same treatment for the Y axis tick labels (e.g. "84 kg") - also canvas text
// drawn natively by Chart.js, with the same softness problem. The native
// ticks stay in the config (color: transparent) purely so Chart.js still
// reserves the correct width for them; these DOM spans are what's visible.
function clearYAxisLabels(wrap) {
    wrap.querySelectorAll('.metamorphosis-efekty__y-label').forEach((el) => el.remove());
}

function renderYAxisLabels(chart) {
    if (!chart.chartArea) {
        return;
    }

    const wrap = chart.canvas.parentElement;
    if (!wrap) {
        return;
    }

    clearYAxisLabels(wrap);

    const offsetLeft = chart.canvas.offsetLeft;
    const offsetTop = chart.canvas.offsetTop;
    const rightEdge = wrap.clientWidth - offsetLeft - chart.chartArea.left + 8;

    chart.scales.y.ticks.forEach((tick) => {
        const y = chart.scales.y.getPixelForValue(tick.value);
        const label = document.createElement('span');
        label.className = 'metamorphosis-efekty__y-label';
        label.textContent = tick.label;
        label.style.right = rightEdge + 'px';
        label.style.top = (offsetTop + y) + 'px';
        wrap.appendChild(label);
    });
}

function createChart(canvas) {
    let values = [];

    try {
        values = JSON.parse(canvas.dataset.values || '[]').map(Number);
    } catch (e) {
        values = [];
    }

    if (!values.length) {
        return null;
    }

    const unit = canvas.dataset.unit || '';

    // Charts for inactive tabs are only created once their panel becomes
    // visible (see ensureChart below) - creating them while display:none left
    // the canvas with a 0-size backing buffer that stayed blurry even after a
    // later resize(). A fixed devicePixelRatio also keeps text/lines crisp on
    // high-DPI phones regardless.
    const chart = new Chart(canvas, {
        type: 'line',
        data: {
            labels: values.map(() => ''),
            datasets: [
                {
                    data: values,
                    borderColor: '#F65700',
                    backgroundColor: (context) => {
                        const { ctx, chartArea } = context.chart;
                        if (!chartArea) {
                            return 'rgba(246, 87, 0, 0.15)';
                        }
                        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                        gradient.addColorStop(0, 'rgba(246, 87, 0, 0.2)');
                        gradient.addColorStop(1, 'rgba(246, 87, 0, 0)');
                        return gradient;
                    },
                    pointBackgroundColor: '#F65700',
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 2,
                    pointRadius: 5,
                    pointHoverRadius: 6,
                    pointHitRadius: 14,
                    borderWidth: 3,
                    tension: 0.4,
                    fill: 'start',
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            devicePixelRatio: window.devicePixelRatio || 2,
            // Chart.js animates points into place on load; our value labels
            // read point positions synchronously right after creation, which
            // without this would race the animation and land on the points'
            // mid-animation (not final) coordinates.
            animation: false,
            layout: {
                padding: {
                    top: 44,
                    right: 40,
                    bottom: 40,
                    left: 8,
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                },
            },
            scales: {
                x: {
                    display: false,
                },
                y: {
                    grid: {
                        color: '#e7e7ee',
                    },
                    ticks: {
                        stepSize: 2,
                        color: 'transparent',
                        callback: (value) => value + (unit ? ' ' + unit : ''),
                    },
                },
            },
            onClick(evt, elements, activeChart) {
                if (!isMobileViewport() || !elements.length) {
                    return;
                }

                const index = elements[0].index;
                activeChart._metamorphosisActiveIndex = activeChart._metamorphosisActiveIndex === index ? null : index;
                renderValueLabels(activeChart);
            },
            onResize(activeChart) {
                syncAxisLabels(activeChart);
                renderValueLabels(activeChart);
                renderYAxisLabels(activeChart);
            },
        },
    });

    syncAxisLabels(chart);
    renderValueLabels(chart);
    renderYAxisLabels(chart);
    chartInstances.set(canvas.id, chart);
    return chart;
}

// Creates the chart the first time its panel is actually visible, reusing it
// afterwards. Reading canvas.dataset.values again on every tab click is cheap
// and keeps this a single code path instead of duplicating creation logic.
function ensureChart(canvas) {
    return chartInstances.get(canvas.id) || createChart(canvas);
}

document.querySelectorAll('.metamorphosis-efekty__panel.is-active .js-metamorphosis-efekty-chart').forEach(ensureChart);

document.querySelectorAll('.js-metamorphosis-efekty-tabs').forEach(function (tabs) {
    tabs.addEventListener('click', function (e) {
        const button = e.target.closest('.metamorphosis-efekty__tab');
        if (!button) {
            return;
        }

        const block = tabs.closest('.metamorphosis-efekty');
        if (!block) {
            return;
        }

        tabs.querySelectorAll('.metamorphosis-efekty__tab').forEach(function (tab) {
            tab.classList.remove('is-active');
        });
        button.classList.add('is-active');

        block.querySelectorAll('.metamorphosis-efekty__panel').forEach(function (panel) {
            panel.classList.toggle('is-active', panel.id === button.dataset.target + '-panel');
        });

        // The target panel is now display:block, so its chart can be safely
        // created here (or resized, if it already existed) with real dimensions
        // - avoids the blurry/misaligned canvas that resulted from building
        // charts while their panel was still hidden.
        const targetPanel = document.getElementById(button.dataset.target + '-panel');
        const targetCanvas = targetPanel ? targetPanel.querySelector('.js-metamorphosis-efekty-chart') : null;
        if (targetCanvas) {
            requestAnimationFrame(() => {
                const chart = ensureChart(targetCanvas);
                if (chart) {
                    chart.resize();
                    syncAxisLabels(chart);
                    renderValueLabels(chart);
                    renderYAxisLabels(chart);
                }
            });
        }
    });
});
